import React, { useState, useCallback, useMemo } from "react";
import { Search, Filter, X, Calendar, Users, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
export interface SearchFilter {
  id: string;
  label: string;
  value: any;
  type: 'text' | 'select' | 'date' | 'dateRange' | 'multiSelect';
  options?: {
    label: string;
    value: string;
  }[];
}
export interface SearchConfig {
  placeholder?: string;
  filters?: {
    id: string;
    label: string;
    type: 'text' | 'select' | 'date' | 'dateRange' | 'multiSelect';
    options?: {
      label: string;
      value: string;
    }[];
    icon?: React.ComponentType<{
      className?: string;
    }>;
  }[];
}
interface AdvancedSearchProps {
  config: SearchConfig;
  onSearch: (query: string, filters: SearchFilter[]) => void;
  className?: string;
  initialQuery?: string;
  initialFilters?: SearchFilter[];
}
export const AdvancedSearch = React.memo(({
  config,
  onSearch,
  className,
  initialQuery = "",
  initialFilters = []
}: AdvancedSearchProps) => {
  const [query, setQuery] = useState(initialQuery || "");
  const [filters, setFilters] = useState<SearchFilter[]>(Array.isArray(initialFilters) ? initialFilters : []);
  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState<Record<string, any>>({});

  // تحديث الحالة عند تغيير الخصائص
  React.useEffect(() => {
    if (initialQuery !== query) {
      setQuery(initialQuery || "");
    }
  }, [initialQuery]);
  React.useEffect(() => {
    if (JSON.stringify(initialFilters) !== JSON.stringify(filters)) {
      setFilters(Array.isArray(initialFilters) ? initialFilters : []);
    }
  }, [initialFilters]);

  // تحديث البحث
  const handleSearch = useCallback(() => {
    try {
      if (onSearch && typeof onSearch === 'function') {
        onSearch(query || "", Array.isArray(filters) ? filters : []);
      }
    } catch (error) {
      console.error("Error in search handler:", error);
    }
  }, [query, filters, onSearch]);

  // إضافة فلتر جديد
  const addFilter = useCallback((filterId: string, value: any) => {
    try {
      if (!filterId || value === null || value === undefined || value === '') return;
      const filterConfig = config?.filters?.find(f => f && f.id === filterId);
      if (!filterConfig) return;
      const existingFilterIndex = filters.findIndex(f => f.id === filterId);
      const newFilter: SearchFilter = {
        id: filterId,
        label: filterConfig.label,
        value,
        type: filterConfig.type,
        options: filterConfig.options
      };
      let newFilters: SearchFilter[];
      if (existingFilterIndex >= 0) {
        newFilters = [...filters];
        newFilters[existingFilterIndex] = newFilter;
      } else {
        newFilters = [...filters, newFilter];
      }
      setFilters(newFilters);
      setTempFilters(prev => ({
        ...prev,
        [filterId]: undefined
      }));
    } catch (error) {
      console.error("Error adding filter:", error);
    }
  }, [filters, config?.filters]);

  // حذف فلتر
  const removeFilter = useCallback((filterId: string) => {
    setFilters(prev => prev.filter(f => f.id !== filterId));
  }, []);

  // مسح جميع الفلاتر
  const clearAllFilters = useCallback(() => {
    setFilters([]);
    setQuery("");
    setTempFilters({});
    onSearch("", []);
  }, [onSearch]);

  // تنسيق قيمة الفلتر للعرض
  const formatFilterValue = useCallback((filter: SearchFilter) => {
    switch (filter.type) {
      case 'date':
        return format(new Date(filter.value), 'yyyy-MM-dd');
      case 'dateRange':
        const {
          from,
          to
        } = filter.value;
        return `${format(from, 'yyyy-MM-dd')} - ${format(to, 'yyyy-MM-dd')}`;
      case 'multiSelect':
        return Array.isArray(filter.value) ? filter.value.join(', ') : filter.value;
      default:
        return filter.value?.toString() || '';
    }
  }, []);

  // عدد الفلاتر النشطة
  const activeFiltersCount = useMemo(() => filters.length, [filters]);
  return <div className={cn("space-y-4", className)}>
      {/* شريط البحث الرئيسي */}
      <div className="flex gap-2 px-px py-[18px]">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder={config.placeholder || "البحث..."} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="pr-10 pl-4 h-10 transition-colors focus:ring-2 focus:ring-primary/20" />
        </div>
        
        <Button onClick={handleSearch} className="px-4 h-10 bg-primary hover:bg-primary-hover transition-colors">
          <Search className="h-4 w-4 ml-2" />
          بحث
        </Button>
        
        {config.filters && config.filters.length > 0 && <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className={cn("px-4 h-10 relative transition-colors", showFilters && "bg-accent")}>
            <Filter className="h-4 w-4 ml-2" />
            فلاتر
            {activeFiltersCount > 0 && <Badge variant="destructive" className="absolute -top-2 -left-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                {activeFiltersCount}
              </Badge>}
          </Button>}
      </div>

      {/* الفلاتر النشطة */}
      {activeFiltersCount > 0 && <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground">الفلاتر النشطة:</span>
          {filters.map(filter => <Badge key={filter.id} variant="secondary" className="bg-primary-light text-primary px-3 py-1 rounded-full flex items-center gap-2">
              <span className="text-xs">
                {filter.label}: {formatFilterValue(filter)}
              </span>
              <X className="h-3 w-3 cursor-pointer hover:text-destructive" onClick={() => removeFilter(filter.id)} />
            </Badge>)}
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs text-muted-foreground hover:text-destructive">
            مسح الكل
          </Button>
        </div>}

      {/* لوحة الفلاتر المتقدمة */}
      {showFilters && config.filters && <div className="bg-card rounded-lg border p-4 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-card-foreground">الفلاتر المتقدمة</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.filters.map(filterConfig => <FilterInput key={filterConfig.id} config={filterConfig} value={tempFilters[filterConfig.id]} onChange={value => {
          if (value !== null && value !== undefined && value !== '') {
            addFilter(filterConfig.id, value);
          }
        }} onTempChange={value => {
          setTempFilters(prev => ({
            ...prev,
            [filterConfig.id]: value
          }));
        }} />)}
          </div>
        </div>}
    </div>;
});

// مكون إدخال الفلتر
interface FilterInputProps {
  config: SearchConfig['filters'][0];
  value: any;
  onChange: (value: any) => void;
  onTempChange: (value: any) => void;
}
const FilterInput = React.memo(({
  config,
  value,
  onChange,
  onTempChange
}: FilterInputProps) => {
  const Icon = config.icon;
  switch (config.type) {
    case 'text':
      return <div className="space-y-2">
          <label className="text-sm font-medium text-card-foreground flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4" />}
            {config.label}
          </label>
          <Input value={value || ''} onChange={e => onTempChange(e.target.value)} onBlur={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onChange(value)} className="transition-colors" />
        </div>;
    case 'select':
      return <div className="space-y-2">
          <label className="text-sm font-medium text-card-foreground flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4" />}
            {config.label}
          </label>
          <Select onValueChange={onChange} value={value}>
            <SelectTrigger>
              <SelectValue placeholder={`اختر ${config.label}`} />
            </SelectTrigger>
            <SelectContent>
              {config.options?.map(option => <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>;
    case 'date':
      return <div className="space-y-2">
          <label className="text-sm font-medium text-card-foreground flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4" />}
            {config.label}
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-right font-normal", !value && "text-muted-foreground")}>
                <Calendar className="ml-2 h-4 w-4" />
                {value ? format(new Date(value), 'yyyy-MM-dd') : `اختر ${config.label}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent mode="single" selected={value ? new Date(value) : undefined} onSelect={date => onChange(date)} initialFocus />
            </PopoverContent>
          </Popover>
        </div>;
    default:
      return null;
  }
});
AdvancedSearch.displayName = "AdvancedSearch";
FilterInput.displayName = "FilterInput";